import { useQueryClient } from '@tanstack/react-query';
import constate from 'constate';
import { useEffect, useMemo, useState } from 'react';
import { useVizFrameworkConfig } from '@/plugin-system';
import { PromQLBuilderProps } from './PromQLBuilder';
import {
  BuilderState,
  filterToMatcher,
  generateQuery,
  MetricMetadata,
  parseQuery,
  UNKNOWN_METRIC_METADATA,
} from './promql-builder-model';

/**
 * Stores the shared context used by the PromQL query builder.
 */
function useBuilder({
  value,
  onChange,
  hideRange,
  hideCalculation,
}: {
  value: PromQLBuilderProps['value'];
  onChange: PromQLBuilderProps['onChange'];
  hideRange?: boolean;
  hideCalculation?: boolean;
}) {
  // Query client is needed to populate metric metadata when the builder is loaded.
  const queryClient = useQueryClient();
  const { promClient } = useVizFrameworkConfig() ?? {};

  const [isLoading, setIsLoading] = useState(false);
  const [metricMetadata, setMetricMetadata] = useState<MetricMetadata>(UNKNOWN_METRIC_METADATA);

  // Populate metricType and isClassicHistogramMetric when the builder is loaded for the first time.
  useEffect(() => {
    const loadBuilder = async () => {
      try {
        const metadata = await fetchMetricMetadata(metric);
        setMetricMetadata(metadata);
        updateQueryOnLoad(value, metadata);
      } catch (err) {
        console.error('Error while fetching metric type', err);
        updateQueryOnLoad(value, metricMetadata);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadBuilder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchMetricMetadata(metric: string) {
    try {
      setIsLoading(true);
      if (!promClient) {
        console.error('Prometheus client not available');
        return UNKNOWN_METRIC_METADATA;
      }
      const metadata = await queryClient.fetchQuery({
        queryKey: ['metric-metadata', metric],
        queryFn: () => promClient.metadataClient.getMetricInfo(metric),
      });
      return {
        metricType: metadata?.type ?? undefined,
        isClassicHistogramMetric: metadata?.isClassicHistogram ?? false,
      };
    } catch (err) {
      console.error('Error while fetching metric metadata', err);
      return UNKNOWN_METRIC_METADATA;
    } finally {
      setIsLoading(false);
    }
  }

  function stripHiddenFields(state: BuilderState): BuilderState {
    return {
      ...state,
      range: hideRange ? undefined : state.range,
      calculation: hideCalculation ? undefined : state.calculation,
    };
  }

  // Make sure the query matches the builderState when the builder is loaded.
  function updateQueryOnLoad(value: string, metadata: MetricMetadata) {
    const generatedQuery = generateQuery(stripHiddenFields(parseQuery(value, metadata)), metadata);
    if (value !== generatedQuery) {
      onChange(generatedQuery, { shouldTriggerOnChange: true });
    }
  }

  // Step 1: Derive builderState from `value`.
  // `value` is the source of truth.
  const builderState: BuilderState = useMemo(() => {
    return parseQuery(value, metricMetadata);
  }, [value, metricMetadata]);

  const { metric, filters, range, calculation } = builderState;

  // Step 2: Detect changes to builder.
  // Use changes to generate a query and update `value`.
  function onMetricChange(metric: BuilderState['metric']) {
    onBuilderChange({ metric }, metricMetadata);
  }

  function onFiltersChange(filters: BuilderState['filters']) {
    onBuilderChange({ filters }, metricMetadata);
  }

  function onRangeChange(range: BuilderState['range']) {
    onBuilderChange({ range }, metricMetadata);
  }

  function onCalculationChange(calculation: BuilderState['calculation']) {
    onBuilderChange({ calculation }, metricMetadata);
  }

  function onBuilderChange(newState: Partial<BuilderState>, metadata: MetricMetadata) {
    updateQuery({ ...builderState, ...newState }, metadata);
  }

  function updateQuery(newState: BuilderState, metadata: MetricMetadata) {
    const newQuery = generateQuery(stripHiddenFields(newState), metadata);
    onChange(newQuery, { shouldTriggerOnChange: true });
  }

  // Derived state.
  const hasQuery = value !== '';
  const hasMetric = metric !== '';
  const hasMetricType = metricMetadata.metricType !== undefined;
  const hasFilters = filters.length > 0;
  const hasRange = range !== undefined;
  const hasCalculation = calculation !== undefined;

  // Expose state to consumers.
  return {
    promClient,
    value,
    onChange,
    isLoading,
    fetchMetricMetadata,
    setMetricMetadata,
    metricType: metricMetadata.metricType,
    isClassicHistogramMetric: metricMetadata.isClassicHistogramMetric,
    metric,
    filters,
    range,
    calculation,
    hasQuery,
    hasMetric,
    hasMetricType,
    hasFilters,
    hasRange,
    hasCalculation,
    onMetricChange,
    onFiltersChange,
    onRangeChange,
    onCalculationChange,
    onBuilderChange,
  };
}

export const [BuilderProvider, useBuilderContext] = constate(useBuilder);

/**
 * Returns a list of Matchers based on the current filters.
 */
function useMatchers() {
  const { filters } = useBuilderContext();
  return useMemo(
    () =>
      filters.map((f) => {
        return filterToMatcher(f);
      }),
    [filters]
  );
}

/**
 * Returns a list of Matchers that exclude the given label.
 */
export function useLabelMatchers(labelName: string) {
  const matchers = useMatchers();
  return useMemo(
    () =>
      matchers.filter((m) => {
        return m.name !== labelName;
      }),
    [matchers, labelName]
  );
}

/**
 * Whether the PromQL editor is in "code mode" or "builder mode".
 */
export type PromQLMode = 'code' | 'builder';

/**
 * Stores state about whether the PromQL editor is in "code mode" or "builder mode".
 */
function usePromQLMode({
  initialMode = 'code',
  setLastUsedMode,
}: {
  initialMode: PromQLMode;
  setLastUsedMode: (mode: PromQLMode) => void;
}) {
  const [mode, setMode] = useState<PromQLMode>(initialMode);
  return {
    mode,
    setMode: (mode: PromQLMode) => {
      setMode(mode);
      setLastUsedMode(mode);
    },
  };
}
export const [PromQLModeProvider, usePromQLModeContext] = constate(usePromQLMode);
